import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraduationCap, Loader2, RefreshCw, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * ProfessorVerdictCard — one LLM call, editorial verdict on a compare
 * table. Fully cached: after the first generation the verdict is stored
 * in Mongo keyed by hash(kind+sorted ids) so repeat opens are instant.
 * Reveals a `?verdict_id=abc123` URL that any recipient can open — no
 * login, no LLM budget consumed.
 */
export default function ProfessorVerdictCard({ kind, ids }) {
  const [params, setParams] = useSearchParams();
  const verdictIdFromUrl = params.get("verdict_id");

  const [state, setState] = useState("idle");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // On mount / whenever the verdict_id in the URL changes, try a cheap
  // lookup. This is what makes `?verdict_id=...` share links land
  // immediately for everyone.
  useEffect(() => {
    if (!verdictIdFromUrl) return;
    let cancelled = false;
    (async () => {
      setState("loading");
      try {
        const { data } = await api.get(`/compare/verdict/${verdictIdFromUrl}`);
        if (cancelled) return;
        setData(data);
        setState("ready");
      } catch {
        // Silent fallback — recipient can still ask afresh.
        if (!cancelled) setState("idle");
      }
    })();
    return () => { cancelled = true; };
  }, [verdictIdFromUrl]);

  if (!ids || ids.length < 2) return null;

  const run = async () => {
    setState("loading");
    setError(null);
    try {
      const { data } = await api.post("/compare/interpret", { kind, ids });
      setData(data);
      setState("ready");
      // Persist to URL so any subsequent open — or a share link — jumps
      // straight to the cached verdict without touching the LLM.
      if (data?.verdict_id) {
        const next = new URLSearchParams(params);
        next.set("verdict_id", data.verdict_id);
        setParams(next, { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 503
          ? "The Professor is busy — try again in a few seconds."
          : err?.response?.data?.detail || "The Professor is unavailable.";
      setError(msg);
      setState("error");
      toast.error(msg);
    }
  };

  const shareVerdict = async () => {
    try {
      const url = window.location.href;
      if (navigator.share) {
        try { await navigator.share({ title: "The Professor's verdict", url }); return; } catch {}
      }
      await navigator.clipboard.writeText(url);
      toast.success("Verdict link copied. Anyone with it sees the same reading — free and instant.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  // Idle — big call-to-action so a first-time visitor understands the option
  // exists. Once fired, the button disappears and the verdict card lands.
  if (state === "idle") {
    return (
      <div
        data-testid="ask-professor-cta"
        className="my-6 p-6 sm:p-8 rounded-xl border-2 border-dashed border-[#4C1D95]/40 bg-[#F5F1EA]/50 print:hidden"
      >
        <div className="flex items-start gap-4 flex-wrap">
          <GraduationCap className="h-8 w-8 text-[#4C1D95] shrink-0" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <div className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
              The Professor
            </div>
            <div className="font-editorial text-2xl sm:text-3xl leading-tight text-stone-900 mt-1">
              Ask The Professor to compare these
            </div>
            <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-xl">
              A 3-paragraph editorial verdict on which of your {ids.length}{" "}
              {kind === "degree" ? "degrees" : "universities"} suits which type of student — grounded
              in the exact numbers in the table below.
            </p>
          </div>
          <button
            type="button"
            data-testid="ask-professor-btn"
            onClick={run}
            className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#4C1D95] text-white hover:bg-[#3B0764] transition-colors text-sm font-underground-sc tracking-wider"
          >
            <GraduationCap className="h-4 w-4" />
            Ask
          </button>
        </div>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div
        data-testid="ask-professor-loading"
        className="my-6 p-8 rounded-xl border-2 border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F1EA] via-white to-[#F5F1EA]/40 shadow-sm"
      >
        <div className="flex items-center gap-3 text-[#4C1D95]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
            The Professor is comparing your {ids.length}{" "}
            {kind === "degree" ? "degrees" : "universities"}…
          </span>
        </div>
        <div className="mt-5 space-y-3">
          <div className="h-4 bg-stone-200 rounded animate-pulse w-full" />
          <div className="h-4 bg-stone-200 rounded animate-pulse w-11/12" />
          <div className="h-4 bg-stone-200 rounded animate-pulse w-4/5" />
        </div>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        data-testid="ask-professor-error"
        className="my-6 p-6 rounded-xl border-2 border-[#4C1D95]/25 bg-[#F5F1EA]/40 print:hidden"
      >
        <div className="text-sm text-stone-700 mb-3">{error}</div>
        <button
          type="button"
          onClick={run}
          data-testid="ask-professor-retry"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4C1D95] text-white text-xs font-underground-sc tracking-wider hover:bg-[#3B0764]"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    );
  }

  // Ready
  return (
    <div
      data-testid="ask-professor-verdict"
      className="my-6 p-6 sm:p-8 rounded-xl border-2 border-[#4C1D95]/25 bg-gradient-to-br from-[#F5F1EA] via-white to-[#F5F1EA]/40 shadow-sm"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <GraduationCap className="h-5 w-5 text-[#4C1D95]" strokeWidth={2.25} />
          <span className="label-uppercase !text-xs tracking-[0.15em] text-[#4C1D95]">
            The Professor · verdict
          </span>
          {data.cached && (
            <span className="text-[10px] font-underground-sc tracking-wider text-stone-400 print:hidden">
              · cached
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 print:hidden">
          <button
            type="button"
            onClick={shareVerdict}
            data-testid="ask-professor-share"
            className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
            title="Copy a share link to this verdict — free for recipients"
          >
            <Share2 className="h-3 w-3" />
            share verdict
          </button>
          <button
            type="button"
            onClick={run}
            data-testid="ask-professor-refresh"
            className="text-[10px] font-underground-sc tracking-wider text-stone-500 hover:text-[#4C1D95]"
          >
            re-ask
          </button>
        </div>
      </div>

      {/* Opening paragraph */}
      <p className="mt-4 text-[18px] sm:text-[19px] leading-[1.6] text-stone-900 font-editorial">
        {data.summary}
      </p>

      {/* Per-item archetype recommendations */}
      <div className="mt-6 space-y-4">
        {(data.verdicts || []).map((v) => (
          <div
            key={v.id}
            data-testid={`professor-verdict-${v.id}`}
            className="pl-4 border-l-[3px] border-[#4C1D95]/50"
          >
            <div className="label-uppercase !text-[10px] tracking-[0.15em] text-[#4C1D95]/80">
              {v.student_type}
            </div>
            <p className="mt-1 text-[15px] leading-relaxed text-stone-800">
              {v.verdict}
            </p>
          </div>
        ))}
      </div>

      {/* Bottom line */}
      {data.bottom_line && (
        <div className="mt-6 pt-4 border-t border-stone-200">
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">
            Bottom line
          </div>
          <p className="text-[15px] leading-relaxed text-stone-800 italic">
            {data.bottom_line}
          </p>
        </div>
      )}
    </div>
  );
}
